import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { CARD_FIELDS, FIELD_LABELS, getCard, getTheme, newCardSlug } from '../utils/cards';
import { validateCardData, sanitizeCardData } from '../utils/validators';
import { prepareLogo, removeStoredImage, resizeImage, uploadImage } from '../utils/storage';
import { DEFAULT_THEME, normalizeLayout, resolveTheme } from '../utils/theme';
import { useAuth } from '../lib/auth';
import AppHeader from './AppHeader';
import CardDisplay from './CardDisplay';
import Loading from './Loading';
import PhoneFrame from './PhoneFrame';
import { errorBox, inputClass, primaryButton, secondaryButton } from './AuthLayout';
import { ColorField, ImagePicker, LayoutPicker, LockNote, Section, Toggle } from './EditorParts';
import { hasPaidAccess } from '../utils/billing';
import { UpgradeNote } from './Upgrade';

const emptyForm = Object.fromEntries(CARD_FIELDS.map((f) => [f, '']));
const PLACEHOLDERS = {
  title: 'e.g. Sales Manager', company: 'e.g. Acme Inc.', department: 'e.g. Sales',
  email: 'you@company.com', phone: '+1 305 555 0100', website: 'https://',
  linkedin: 'https://linkedin.com/in/...', twitter: 'https://x.com/...', instagram: 'https://instagram.com/...',
};
const GROUPS = [
  { title: 'Name and role', fields: ['full_name', 'title', 'company', 'department'] },
  { title: 'Contact details', fields: ['phone', 'email', 'website', 'linkedin', 'instagram', 'twitter'] },
];

/** /cards/:cardId/edit and /cards/new/:orgId */
export default function CardEdit() {
  const { cardId, orgId: newOrgId } = useParams();
  const { user, memberships, canManageOrg } = useAuth();
  const navigate = useNavigate();

  const [card, setCard] = useState(null);
  const [orgId, setOrgId] = useState(newOrgId || null);
  const [form, setForm] = useState(emptyForm);
  const [layout, setLayout] = useState(null); // this card's own layout; null = company default
  const [leadCapture, setLeadCapture] = useState(true);
  const [theme, setTheme] = useState(null);
  const [design, setDesign] = useState(DEFAULT_THEME);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [orgInfo, setOrgInfo] = useState(null); // for platform admins editing a card outside their memberships
  const org = memberships.find((m) => m.org_id === orgId)?.organizations || orgInfo;
  const isCompany = org?.kind === 'company';
  const canManage = orgId ? canManageOrg(orgId) : false;
  // Personal accounts set colors and logo here; companies do it in Company → Brand
  const editsDesign = canManage && !isCompany;
  // Locked fields are a Teams feature: they only apply while the company has paid access
  const locked = useMemo(() => (canManage || !hasPaidAccess(org) ? [] : theme?.locked_fields || []), [canManage, theme, org]);
  const layoutLocked = locked.includes('layout');
  const leadForced = !!theme?.force_lead_capture;

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        let existing = null;
        let targetOrg = newOrgId;
        if (cardId) {
          existing = await getCard(cardId);
          if (!existing) throw new Error('Card not found.');
          targetOrg = existing.org_id;
        }
        const t = await getTheme(targetOrg);
        if (!memberships.some((m) => m.org_id === targetOrg)) {
          const { data: o } = await supabase.from('organizations').select('id, name, kind, status, billing_status, comp_until').eq('id', targetOrg).maybeSingle();
          if (active) setOrgInfo(o);
        }
        if (!active) return;
        setCard(existing);
        setOrgId(targetOrg);
        setTheme(t);
        setDesign({ ...DEFAULT_THEME, ...(t || {}), layout: normalizeLayout(t?.layout) });
        setLayout(existing?.layout ? normalizeLayout(existing.layout) : null);
        setLeadCapture(existing ? existing.lead_capture !== false : true);
        setForm(existing
          ? Object.fromEntries(CARD_FIELDS.map((f) => [f, existing[f] || '']))
          : { ...emptyForm, email: user.email || '' });
      } catch (err) {
        if (active) setGeneralError(err.message || 'Could not load the card.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [cardId, newOrgId, user.email]);

  // Default the company name for company cards
  useEffect(() => {
    if (!card && isCompany) setForm((f) => (f.company ? f : { ...f, company: org.name }));
  }, [card, isCompany, org]);

  const photoPreview = useMemo(() => {
    if (photoFile) return URL.createObjectURL(photoFile);
    return photoRemoved ? null : card?.photo_url || null;
  }, [photoFile, photoRemoved, card]);
  const logoPreview = useMemo(() => (logoFile ? URL.createObjectURL(logoFile) : design.logo_url || null), [logoFile, design.logo_url]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(({ [name]: _, ...rest }) => rest);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setGeneralError('');
    const data = sanitizeCardData(form);
    const validation = validateCardData(data);
    if (!validation.isValid) return setErrors(validation.errors);

    setSaving(true);
    try {
      const fields = Object.fromEntries(
        CARD_FIELDS.filter((f) => !locked.includes(f)).map((f) => [f, data[f] || null])
      );
      if (!layoutLocked) fields.layout = layout;
      if (!leadForced) fields.lead_capture = leadCapture;

      let saved;
      if (card) {
        const { data: row, error } = await supabase.from('cards').update(fields).eq('id', card.id).select().single();
        if (error) throw error;
        saved = row;
      } else {
        const { data: row, error } = await supabase
          .from('cards')
          .insert({ ...fields, org_id: orgId, user_id: user.id, slug: newCardSlug(data.full_name), status: 'active' })
          .select()
          .single();
        if (error) throw error;
        saved = row;
      }

      if (!locked.includes('photo_url')) {
        if (photoFile) {
          const blob = await resizeImage(photoFile, 800, 800);
          const url = await uploadImage('card-photos', `${orgId}/${saved.id}`, blob);
          const { data: row, error } = await supabase.from('cards').update({ photo_url: url }).eq('id', saved.id).select().single();
          if (error) throw error;
          saved = row;
          // The row now points at the new file; the old one is no longer used
          if (card?.photo_url && card.photo_url !== url) await removeStoredImage('card-photos', card.photo_url);
        } else if (photoRemoved && card?.photo_url) {
          const { error } = await supabase.from('cards').update({ photo_url: null }).eq('id', saved.id);
          if (error) throw error;
          await removeStoredImage('card-photos', card.photo_url);
        }
      }

      if (editsDesign) {
        let logo_url = design.logo_url || null;
        if (logoFile) logo_url = await uploadImage('brand-logos', orgId, await prepareLogo(logoFile));
        const themeRow = {
          layout: layout || design.layout,
          primary_color: design.primary_color,
          accent_color: design.accent_color,
          locked_fields: design.locked_fields || [],
          logo_url,
        };
        const { error } = theme
          ? await supabase.from('brand_themes').update(themeRow).eq('org_id', orgId)
          : await supabase.from('brand_themes').insert({ ...themeRow, org_id: orgId });
        if (error) throw error;
        if (theme?.logo_url && theme.logo_url !== logo_url) await removeStoredImage('brand-logos', theme.logo_url);
      }

      navigate('/', { replace: true });
    } catch (err) {
      console.error('Save card error:', err);
      setGeneralError(err.message || 'Could not save the card. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  const previewCard = { ...(card || {}), ...form, full_name: form.full_name || 'Your name', photo_url: photoPreview };
  const previewTheme = editsDesign ? { ...design, logo_url: logoPreview } : theme;
  const previewLayout = layoutLocked
    ? normalizeLayout(theme?.layout)
    : layout || normalizeLayout(editsDesign ? design.layout : theme?.layout);
  const t = resolveTheme(previewTheme);

  return (
    <div className="min-h-screen bg-surface">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <Link to="/" className="text-sm font-medium text-ink-muted hover:text-ink">← My cards</Link>
            <h1 className="text-[32px] leading-[38px] font-extrabold tracking-tight mt-1">{card ? 'Edit your card' : 'Create your card'}</h1>
          </div>
          {card && (
            <Link to={`/cards/${card.id}/print`} className="inline-flex items-center gap-2 text-sm font-semibold text-ink-muted hover:text-ink">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <rect x="2" y="5" width="20" height="14" rx="2.5" /><path d="M15 9.5a3.5 3.5 0 0 1 0 5M17.5 8a6 6 0 0 1 0 8" />
              </svg>
              NFC card design
            </Link>
          )}
        </div>

        <div className="grid lg:grid-cols-[minmax(0,1fr)_380px] gap-8 items-start">
          <form onSubmit={handleSave} className="space-y-5 min-w-0">
            <Section title="Profile photo" hint="Your photo appears on every layout. Without one, your card shows your initials.">
              <ImagePicker
                preview={photoPreview}
                fallback={previewCard.full_name}
                t={t}
                onPick={(f) => { setPhotoFile(f); setPhotoRemoved(false); }}
                onRemove={() => { setPhotoFile(null); setPhotoRemoved(true); }}
                disabled={saving || locked.includes('photo_url')}
                lockedNote={locked.includes('photo_url') ? 'Set by your company' : ''}
                round
                label={photoPreview ? 'Change photo' : 'Upload photo'}
                help="JPG, PNG or WebP. A square photo works best."
              />
            </Section>

            {GROUPS.map((g) => (
              <Section key={g.title} title={g.title}>
                <div className="grid sm:grid-cols-2 gap-4">
                  {g.fields.map((f) => (
                    <label key={f} className={`block ${f === 'full_name' ? 'sm:col-span-2' : ''}`}>
                      <span className="flex items-center gap-1.5 text-sm font-semibold text-ink mb-1.5">
                        {FIELD_LABELS[f]} {f === 'full_name' && <span className="text-coral-ink">*</span>}
                        {locked.includes(f) && <LockNote />}
                      </span>
                      <input
                        name={f}
                        value={form[f]}
                        onChange={onChange}
                        type={f === 'email' ? 'email' : f === 'phone' ? 'tel' : 'text'}
                        placeholder={PLACEHOLDERS[f] || ''}
                        required={f === 'full_name'}
                        disabled={saving || locked.includes(f)}
                        className={`${inputClass} ${errors[f] ? 'border-coral-ink' : ''}`}
                      />
                      {errors[f] && <span className="text-coral-ink text-sm">{errors[f]}</span>}
                    </label>
                  ))}
                </div>
              </Section>
            ))}

            <Section
              title="Layout"
              hint={layoutLocked
                ? `${org?.name || 'Your company'} has set the layout for every card.`
                : isCompany ? 'Pick how your card looks. Colors and logo come from your company.' : 'Pick how your card looks.'}
            >
              <LayoutPicker
                value={previewLayout}
                onChange={setLayout}
                disabled={saving || layoutLocked}
                t={t}
              />
              {isCompany && layout && !layoutLocked && (
                <button type="button" onClick={() => setLayout(null)} className="mt-3 text-sm font-medium text-blue hover:underline">
                  Use the company's layout instead
                </button>
              )}
            </Section>

            <Section title="Ask for contact details first"
              hint="When someone taps Save contact, they're invited to share their name, email or phone with you first. They can still download your contact without it.">
              {!hasPaidAccess(org) ? (
                <UpgradeNote org={org} canManage={canManageOrg(orgId)}>
                  Collecting leads is part of {isCompany ? 'Teams' : 'Pro'}. Visitors can still save your contact.
                </UpgradeNote>
              ) : leadForced ? (
                <p className="text-sm font-semibold text-ink">{org?.name || 'Your company'} has turned this on for every card.</p>
              ) : (
                <Toggle checked={leadCapture} onChange={setLeadCapture} disabled={saving} label="Invite visitors to share their details" />
              )}
            </Section>

            {editsDesign ? (
              <Section title="Colors and logo">
                <div className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <ColorField label="Brand color" value={design.primary_color} onChange={(v) => setDesign((d) => ({ ...d, primary_color: v }))} />
                    <ColorField label="Button color" value={design.accent_color} onChange={(v) => setDesign((d) => ({ ...d, accent_color: v }))} />
                  </div>
                  <div>
                    <span className="block text-sm font-semibold text-ink mb-2">Logo</span>
                    <ImagePicker preview={logoPreview} onPick={setLogoFile} disabled={saving} label={logoPreview ? 'Change logo' : 'Upload logo'} allowSvg
                      help="SVG, PNG, JPG or WebP."
                      onRemove={() => { setLogoFile(null); setDesign((d) => ({ ...d, logo_url: null })); }} />
                  </div>
                </div>
              </Section>
            ) : canManage && isCompany ? (
              <div className="bg-blue-soft border border-blue/30 rounded-2xl px-5 py-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-ink">
                  Logo, colors, default layout, locked fields and lead capture for every {org?.name} card are in <strong>Company → Brand</strong>.
                </p>
                <Link to={`/company/${orgId}?tab=brand`} className="text-sm font-semibold text-blue hover:underline whitespace-nowrap">Open company design</Link>
              </div>
            ) : (
              <p className="text-sm text-ink-muted">Colors and logo are set by {org?.name || 'your company'}.</p>
            )}

            {generalError && <div className={errorBox}>{generalError}</div>}
            <div className="flex flex-wrap gap-3 pt-1">
              <button type="submit" disabled={saving} className={`${primaryButton.replace('w-full ', 'w-full sm:w-auto ')} px-6`}>
                {saving ? 'Saving...' : card ? 'Save changes' : 'Create card'}
              </button>
              <Link to="/" className={secondaryButton}>Cancel</Link>
            </div>
          </form>

          <aside className="lg:sticky lg:top-6 min-w-0" aria-label="Live preview">
            <p className="text-xs font-semibold tracking-wider uppercase text-ink-muted mb-3">Live preview</p>
            <PhoneFrame>
              <CardDisplay card={previewCard} theme={previewTheme} layout={previewLayout} className="min-h-[640px] [&>div]:min-h-[640px]" />
            </PhoneFrame>
          </aside>
        </div>
      </main>
    </div>
  );
}
